/* eslint-disable import/no-cycle */
/* eslint-disable max-lines */
import { useState, useEffect, FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import Image from '@base/Image';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import { commas, currencySymbolMap } from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import ArrowBack from '@icon/ArrowBack';
import IconButton from '@base/IconButton/IconButton';
import useWindowSize from '@hooks/useWindowSize';
import { Fonts, Spacings, breakpoints, mediaQuery } from '@styles/index';
import usePaymentMethods from '@hooks/usePaymentMethods';
import Dynamic from '@base/Dynamic/Dynamic';
import { AnyArgVoidReturnFunction } from 'types/utils';
import Typography from 'aquilla/core/atoms/Typography';
import handleAppPaymentRedirection from '@utils/handleAppPaymentRedirection';
import {
  PAYMENT_TYPES,
  STORE_PROCESSOR,
  PaymentMethodLabel
} from './constants/index';
import { getPaymentMethod } from './utils';
import { getTotalAmtBySubscription } from './usePurchaseDetails';
import PaymentMethodItem from './PaymentMethodItem';

const PayInPartsPayment = Dynamic(
  () => import('./PayInParts/PayInPartsPayment')
);
const CardPayment = Dynamic(() => import('./CardPayment'));
const NetBankingPayment = Dynamic(() => import('./NetBankingPayment'));
const UPIPaymentWithQR = Dynamic(() => import('./UPIPayment/UPIPaymentWithQR'));
const UPIPaymentV2 = Dynamic(() => import('./UPIPayment/UPIPaymentV2'));
const WalletsPayment = Dynamic(() => import('./WalletsPayment'));
const EMIPayment = Dynamic(() => import('./EMI/EMIPayment'));
const CashPayment = Dynamic(() => import('./CashPayment/CashPayment'));
const CashReceipt = Dynamic(() => import('./CashPayment/CashReceipt'));
const LoanPayment = Dynamic(() => import('./Loan/LoanSelector'));

const paymentCards = [
  {
    id: 1,
    img: 'payments/phonepe2.svg',
    name: 'PhonePe'
  },
  {
    id: 2,
    img: 'payments/gpay2.svg',
    name: 'GPay'
  },
  {
    id: 3,
    img: 'payments/paytm2.svg',
    name: 'PayTm'
  },
  {
    id: 4,
    img: 'payments/cred2.svg',
    name: 'CRED UPI'
  }
];

const Wrapper = styled.div<{ $mwebCheckoutV2: boolean }>`
  border-radius: ${Spacings.SPACING_2B};
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  padding-right: ${Spacings.SPACING_8B};
  margin-bottom: ${Spacings.SPACING_3B};
  box-shadow: ${boxShadowGenerator()};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: 24px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-bottom: 0px;
    padding: 16px;
    ${
      ({ $mwebCheckoutV2 }) => {
        if ($mwebCheckoutV2)
          return `
            padding: 0px;
            background-color: var(--color-base-light-0);
            box-shadow: none;
        `;
      }
      /* height: calc(100vh - 56px - 16px); */
    }
  }
`;

const PaymentMethodsContainer = styled.div<{
  $isSelected: boolean;
  $mwebCheckoutV2: boolean;
}>`
  display: flex;
  margin-top: ${Spacings.SPACING_8B};
  @media only screen and (max-width: ${breakpoints.tablet}) {
    margin-top: ${({ $isSelected, $mwebCheckoutV2 }) =>
      $isSelected || $mwebCheckoutV2 ? '0px' : '24px'};
  }
`;

const AvailableMethods = styled.div<{ $isSelected: boolean }>`
  position: relative;
  min-width: 188px;
  max-width: 188px;
  background-color: var(--color-base-0);
  padding: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_1B};
  z-index: 1;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    padding: 0px;
    background-color: initial;
    max-width: 100%;
    min-width: 100%;
    width: 100%;
    display: ${({ $isSelected }) => ($isSelected ? 'none' : 'block')};
  }
`;

const SubHeading = styled(Typography)`
  color: var(--color-text-secondary);
`;

const HeadingWrapper = styled.div``;

const MethodSelector = styled.div<{
  height: number;
  top: number;
  $mwebCheckoutV2: boolean;
}>`
  position: absolute;
  height: ${({ height }) => `${height}px`};
  top: ${({ top }) => (top > 0 ? `${top}px` : '16px')};
  left: ${Spacings.SPACING_4B};
  right: ${Spacings.SPACING_4B};
  background-color: white;
  z-index: -1;
  border-radius: ${Spacings.SPACING_2B};
  transition: top 200ms ease-in;
  box-shadow: ${boxShadowGenerator()};
  ${mediaQuery.uptoMobile} {
    ${({ $mwebCheckoutV2 }) => {
      if ($mwebCheckoutV2)
        return `
            background-color: none;
        `;
    }}
  }
`;
const UPIOption = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    background-color: var(--color-base-1);
  }
`;
const UPIHeader = styled.div`
  width: 360px;
  height: 32px;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_4B};
`;
const HeaderText = styled(Typography)`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Fonts.LARGE};
  color: var(--color-text-secondary);
`;

const BaseUPI = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_4B} ${Spacings.SPACING_1B}
    ${Spacings.SPACING_4B};
  gap: ${Spacings.SPACING_2B};
`;
const BaseCard = styled.div`
  width: 76px;
  height: 76px;
  border-radius: ${Spacings.SPACING_2B};
  border: 1px solid #e9eef2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
`;
const UPIAppName = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: 16px;
`;

const PaymentGroupWrapper = styled.div`
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_4B};
  background-color: var(--color-base-1);
`;

const GroupHeading = styled.div`
  padding: 8px 0px;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: var(--color-text-secondary);
`;

const BackContainer = styled.div<{ $mwebCheckoutV2: boolean }>`
  align-items: center;
  display: none;
  @media only screen and (max-width: ${breakpoints.tablet}) {
    display: flex;
    ${({ $mwebCheckoutV2 }) => {
      if ($mwebCheckoutV2)
        return `
          flex-direction: row;
          align-items: start;
          background-color: var(--color-base-1);
          padding: 16px;
        `;
    }}
  }
`;

const BackButton = styled(IconButton)`
  margin-right: ${Spacings.SPACING_4B};
  border: 1px solid var(--color-divider);
`;

const BackButtonNew = styled.div`
  margin-right: ${Spacings.SPACING_4B};
`;

const PartPaymentText = styled(P1)<{ $mwebCheckoutV2: boolean }>`
  margin-top: ${Spacings.SPACING_1B};
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    ${({ $mwebCheckoutV2 }) => {
      if ($mwebCheckoutV2)
        return `
          background-color: var(--color-base-1);
          padding: 16px;
          margin-bottom: 8px;
        `;
    }}
  }
`;

const StyH4 = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_3B};
  padding-left: ${Spacings.SPACING_4B};
`;

const StyArrowBack = styled(KeyboardArrowDown)`
  transform: rotate(90deg);
`;

interface IPaymentMethodsProps {
  createOrder: AnyArgVoidReturnFunction;
  cashPaymentStatus: any;
  partPaymentStatus: any;
  setCashPaymentStatus: AnyArgVoidReturnFunction;
  priceSummaryDrawerProps: any;
}

const PaymentMethods: FC<IPaymentMethodsProps> = ({
  createOrder,
  cashPaymentStatus,
  partPaymentStatus,
  setCashPaymentStatus,
  priceSummaryDrawerProps
}) => {
  const {
    state: {
      isCombo,
      subscription,
      bankSelected,
      setBankSelected,
      creditsApplied,
      totalAmount,
      setTotalAmount,
      partPaymentActive,
      setPartPaymentActive,
      selectedPayInPartsPlan,
      setSelectedPayInPartsPlan,
      selectedMethod,
      setSelectedMethod,
      loanInterstAmt,
      availablePaymentMethods,
      paymentMethodsGrouping,
      mwebCheckoutV2,
      loanStep,
      upiApp,
      setUpiApp,
      setOrder,
      setGenerateQRClicked
    }
  } = usePaymentMethods();
  const size = useWindowSize();

  const router = useRouter();
  const {
    query: { payment_method, goalUID }
  } = router;

  const currency = currencySymbolMap(subscription?.currency);
  const subscriptionTotalAmount = getTotalAmtBySubscription(
    subscription,
    creditsApplied
  );

  const [selectedPosTop, setSelectedPosTop] = useState(0);
  const [selectedPosHeight, setSelectedPosHeight] = useState(0);
  const [selectedMethodCompKey, setSelectedMethodCompKey] = useState(
    Date.now().toString()
  );

  const pip =
    (partPaymentStatus?.isActivePartSet && partPaymentStatus?.partPayments) ||
    selectedPayInPartsPlan;

  const [receiptOpen, setReceiptOpen] = useState(false);

  useEffect(() => {
    setReceiptOpen(!!cashPaymentStatus);
  }, [cashPaymentStatus]);

  useEffect(() => {
    if (partPaymentActive) {
      setTotalAmount(pip?.find((item) => !item.payment)?.amount);
    } else {
      setTotalAmount(subscriptionTotalAmount);
    }
  }, [partPaymentActive, subscriptionTotalAmount, pip, setTotalAmount]);

  const isStore = subscription?.processor_name === STORE_PROCESSOR;

  const onClickPaymentMethod = (type) => {
    if (!partPaymentActive) {
      setSelectedPayInPartsPlan(null);
    }
    setSelectedMethodCompKey(Date.now().toString());
    setSelectedMethod(type);
    setBankSelected(false);
    setOrder(null);
    setGenerateQRClicked(false);
  };

  const handleCreateOrder = (
    paymentMethod,
    paymentMethodType,
    phone = null,
    emiData = null,
    gateway,
    cardType,
    bankName
  ) => {
    createOrder(
      paymentMethod,
      paymentMethodType,
      phone,
      emiData,
      gateway,
      gateway === 8
        ? getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.CASH).data[0]
            .bankName
        : null,
      cardType,
      bankName
    );
  };
  const handleCreateUPIOrder = (name: string) => {
    setUpiApp(name);
  };
  useEffect(() => {
    const el: any = document.querySelector(`[data-id="${selectedMethod}"]`);
    setSelectedPosTop(el?.offsetTop);
    setSelectedPosHeight(el?.clientHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod, availablePaymentMethods]);

  useEffect(() => {
    if (size.width > 1136 && !selectedMethod) {
      setSelectedMethod(
        isStore
          ? PAYMENT_TYPES.CARD
          : availablePaymentMethods[0].metaInfo.paymentTypeId
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod, size.width]);

  useEffect(() => {
    if (size.width > 1136 && partPaymentActive) {
      setSelectedMethod(availablePaymentMethods[0].metaInfo.paymentTypeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partPaymentActive, size.width]);

  const filteredPaymentMethods = useMemo(() => {
    return isStore
      ? availablePaymentMethods.filter(
          ({ metaInfo: { paymentTypeId } }) =>
            paymentTypeId !== PAYMENT_TYPES.PAY_IN_PARTS
        )
      : availablePaymentMethods;
  }, [availablePaymentMethods, isStore]);

  useEffect(() => {
    if (
      selectedMethod &&
      !getPaymentMethod(availablePaymentMethods, selectedMethod)
    ) {
      if (size.width > 1136)
        setSelectedMethod(availablePaymentMethods[0].metaInfo.paymentTypeId);
      else setSelectedMethod(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePaymentMethods, selectedMethod, setSelectedMethod, size.width]);

  const selectedPaymentLabel = useMemo(() => {
    if (!selectedMethod) return '';
    const label =
      loanStep === 2 && selectedMethod === PAYMENT_TYPES.LOAN
        ? 'Setup a call'
        : PaymentMethodLabel[selectedMethod];
    if (label && mwebCheckoutV2) return label;
    return getPaymentMethod(availablePaymentMethods, selectedMethod)?.metaInfo
      ?.paymentTypeName;
  }, [availablePaymentMethods, loanStep, mwebCheckoutV2, selectedMethod]);
  useEffect(() => {
    if (isStore) setSelectedMethod(PAYMENT_TYPES.CARD);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStore, setSelectedMethod]);
  useEffect(() => {
    if (
      payment_method === 'loans' &&
      getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.LOAN)
    )
      setSelectedMethod(PAYMENT_TYPES.LOAN);
    if (
      payment_method === 'pac' &&
      getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.CASH)
    )
      setSelectedMethod(PAYMENT_TYPES.CASH);
    if (
      payment_method === 'pip' &&
      getPaymentMethod(availablePaymentMethods, PAYMENT_TYPES.PAY_IN_PARTS)
    )
      setSelectedMethod(PAYMENT_TYPES.PAY_IN_PARTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment_method, setSelectedMethod]);

  const renderSelectedMethodFlow = () => {
    if (
      !selectedMethod ||
      !getPaymentMethod(availablePaymentMethods, selectedMethod)
    ) {
      return <div />;
    }

    switch (selectedMethod) {
      case PAYMENT_TYPES.PAY_IN_PARTS:
        return (
          <PayInPartsPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            selectedPayInPartsPlan={selectedPayInPartsPlan}
            setSelectedPayInPartsPlan={setSelectedPayInPartsPlan}
            setPartPaymentActive={setPartPaymentActive}
          />
        );

      case PAYMENT_TYPES.CARD:
        return (
          <CardPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            onClickPay={handleCreateOrder}
          />
        );

      case PAYMENT_TYPES.NET_BANKING:
        return (
          <NetBankingPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            onClickPay={handleCreateOrder}
          />
        );

      case PAYMENT_TYPES.UPI:
        if (mwebCheckoutV2)
          return (
            <UPIPaymentV2
              key={selectedMethodCompKey}
              // @ts-ignore
              onClickPay={handleCreateOrder}
              isMandate={availablePaymentMethods?.[0]?.isUpiMandate}
            />
          );

        return (
          <UPIPaymentWithQR
            key={selectedMethodCompKey}
            // @ts-ignore
            isMandate={availablePaymentMethods?.[0]?.isUpiMandate}
            // @ts-ignore
            onClickPay={handleCreateOrder}
          />
        );

      case PAYMENT_TYPES.WALLETS:
        return (
          <WalletsPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            onClickPay={handleCreateOrder}
          />
        );

      case PAYMENT_TYPES.EMI:
        return (
          <EMIPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            onClickPay={handleCreateOrder}
          />
        );

      case PAYMENT_TYPES.CASH:
        if (!receiptOpen) {
          return (
            <CashPayment
              key={selectedMethodCompKey}
              // @ts-ignore
              onClickPay={handleCreateOrder}
              goalUID={subscription?.goal?.uid}
              isCombo={isCombo}
              priceSummaryDrawerProps={priceSummaryDrawerProps}
            />
          );
        }
        return <div />;

      case PAYMENT_TYPES.LOAN:
        return (
          <LoanPayment
            key={selectedMethodCompKey}
            // @ts-ignore
            onClickPay={handleCreateOrder}
            loanInterestAmt={loanInterstAmt}
          />
        );

      default:
        return <div />;
    }
  };

  const renderAvailableMethods = () => {
    if (size.isMobile && mwebCheckoutV2)
      return (
        <>
          {!selectedMethod ? (
            <>
              <>
                {paymentMethodsGrouping.map((methodGroup, index) => (
                  <>
                    {methodGroup.data.map(
                      ({
                        metaInfo: {
                          paymentTypeId,
                          paymentTypeName,
                          image,
                          description
                        }
                      }) => (
                        <>
                          {paymentTypeName === 'UPI' ? (
                            <UPIOption key={paymentTypeId}>
                              <UPIHeader>
                                <HeaderText variant="p2">
                                  Pay via UPI
                                </HeaderText>
                              </UPIHeader>
                              <BaseUPI>
                                {paymentCards.map(({ id, img, name }) => (
                                  <BaseCard key={id}>
                                    <Image
                                      src={img}
                                      alt={name}
                                      width="36px"
                                      height="36px"
                                      onClick={() => handleCreateUPIOrder(name)}
                                    />
                                    <UPIAppName variant="p3">{name}</UPIAppName>
                                  </BaseCard>
                                ))}
                              </BaseUPI>
                              <PaymentMethodsContainer
                                $isSelected={selectedMethod}
                                $mwebCheckoutV2={mwebCheckoutV2}
                              >
                                <AvailableMethods $isSelected={selectedMethod}>
                                  <MethodSelector
                                    top={selectedPosTop}
                                    height={selectedPosHeight}
                                    $mwebCheckoutV2={mwebCheckoutV2}
                                  />
                                  <>
                                    <PaymentGroupWrapper
                                      key={`group-${String(index)}`}
                                    >
                                      <>
                                        {paymentTypeName === 'UPI' ? (
                                          <PaymentMethodItem
                                            key={paymentTypeId}
                                            type={paymentTypeId}
                                            label={paymentTypeName}
                                            description={description}
                                            isSelected={
                                              paymentTypeId === selectedMethod
                                            }
                                            onClick={onClickPaymentMethod}
                                            imageSrc={image}
                                            mwebCheckoutV2
                                          />
                                        ) : (
                                          <></>
                                        )}
                                      </>
                                    </PaymentGroupWrapper>
                                  </>
                                </AvailableMethods>

                                {renderSelectedMethodFlow()}
                              </PaymentMethodsContainer>
                            </UPIOption>
                          ) : (
                            <></>
                          )}
                        </>
                      )
                    )}
                  </>
                ))}
              </>
            </>
          ) : (
            <></>
          )}
          <PaymentMethodsContainer
            $isSelected={selectedMethod}
            $mwebCheckoutV2={mwebCheckoutV2}
          >
            <AvailableMethods $isSelected={selectedMethod}>
              <MethodSelector
                top={selectedPosTop}
                height={selectedPosHeight}
                $mwebCheckoutV2={mwebCheckoutV2}
              />
              {availablePaymentMethods?.[0]?.isUpiMandate ? (
                <></>
              ) : (
                <StyH4 variant="h4">Payment methods</StyH4>
              )}
              {paymentMethodsGrouping.map((methodGroup, index) => (
                <>
                  <PaymentGroupWrapper key={`group-${String(index)}`}>
                    {methodGroup.data.map(
                      ({
                        metaInfo: {
                          paymentTypeId,
                          paymentTypeName,
                          image,
                          description
                        }
                      }) => {
                        return (
                          <>
                            {paymentTypeName !== 'UPI' ? (
                              <PaymentMethodItem
                                key={paymentTypeId}
                                type={paymentTypeId}
                                label={paymentTypeName}
                                description={description}
                                isSelected={paymentTypeId === selectedMethod}
                                onClick={onClickPaymentMethod}
                                imageSrc={image}
                                mwebCheckoutV2
                              />
                            ) : (
                              <></>
                            )}
                          </>
                        );
                      }
                    )}
                  </PaymentGroupWrapper>
                </>
              ))}
            </AvailableMethods>
            {renderSelectedMethodFlow()}
          </PaymentMethodsContainer>
        </>
      );
    return (
      <PaymentMethodsContainer
        $isSelected={bankSelected}
        $mwebCheckoutV2={mwebCheckoutV2}
      >
        <AvailableMethods $isSelected={selectedMethod}>
          <MethodSelector
            top={selectedPosTop}
            height={selectedPosHeight}
            $mwebCheckoutV2={mwebCheckoutV2}
          />
          {filteredPaymentMethods.map(
            ({ metaInfo: { paymentTypeId, paymentTypeName, image } }) => {
              return (
                <PaymentMethodItem
                  key={paymentTypeId}
                  type={paymentTypeId}
                  label={paymentTypeName}
                  isSelected={paymentTypeId === selectedMethod}
                  onClick={onClickPaymentMethod}
                  imageSrc={image}
                />
              );
            }
          )}
        </AvailableMethods>
        {renderSelectedMethodFlow()}
      </PaymentMethodsContainer>
    );
  };

  const subText = `Part ${
    pip?.findIndex((item) => !item.payment) + 1
  } · ${currency}${commas(Math.round(totalAmount))}`;

  if (receiptOpen) {
    const goalToSelect = isCombo
      ? subscription.childSubscriptions[0].goal.uid
      : goalUID;
    const isRedirected = handleAppPaymentRedirection(
      router.query,
      'cash',
      null,
      goalToSelect
    );
    if (isRedirected) return;
    return (
      <CashReceipt
        // @ts-ignore
        goalUID={subscription?.goal?.uid}
        isCombo={isCombo}
        setReceiptOpen={setReceiptOpen}
        cashResponse={cashPaymentStatus}
        setCashPaymentStatus={setCashPaymentStatus}
        priceSummaryDrawerProps={priceSummaryDrawerProps}
      />
    );
  }

  return (
    <>
      <Wrapper $mwebCheckoutV2={mwebCheckoutV2}>
        {!mwebCheckoutV2 && (!selectedMethod || size.width > 1136) ? (
          <H4 as="h2">Choose a payment method</H4>
        ) : (
          !mwebCheckoutV2 &&
          !bankSelected && (
            <BackContainer $mwebCheckoutV2={mwebCheckoutV2}>
              <BackButton onClick={() => setSelectedMethod(null)}>
                <ArrowBack />
              </BackButton>
              <H4>{selectedPaymentLabel}</H4>
            </BackContainer>
          )
        )}

        {mwebCheckoutV2 && selectedMethod && !bankSelected && (
          <BackContainer $mwebCheckoutV2={mwebCheckoutV2}>
            <BackButtonNew
              onClick={() => {
                setSelectedMethod(null);
              }}
            >
              <StyArrowBack size="24px" color="var(--color-text-primary)" />
            </BackButtonNew>
            <HeadingWrapper>
              <Typography variant="h5">{selectedPaymentLabel}</Typography>
            </HeadingWrapper>
          </BackContainer>
        )}

        {partPaymentActive && !mwebCheckoutV2 && (
          <PartPaymentText $mwebCheckoutV2={mwebCheckoutV2}>
            {subText}
          </PartPaymentText>
        )}
        {renderAvailableMethods()}
      </Wrapper>
    </>
  );
};

export default PaymentMethods;
